import { Card, Group, Stack, Text, Title, useMantineTheme } from '@mantine/core';
import type { DashboardRow } from '../api-client.ts';
import type { ZoneWithOccupancy } from '../hooks/useDashboard.ts';
import { tokens } from '../ui/tokens.ts';

interface Props {
  readonly appTitle: string;
  readonly venueName: string;
  readonly tagline: string;
  readonly dateLabel: string;
  readonly rows: readonly DashboardRow[];
  readonly zones: readonly ZoneWithOccupancy[];
  readonly unitLabel: string;
}

function countGuests(rows: readonly DashboardRow[]): number {
  return rows
    .filter((r) => r.status !== 'canceled' && r.status !== 'no_show')
    .reduce((sum, r) => sum + r.partySize, 0);
}

export function TodayStatsHero(props: Props) {
  const theme = useMantineTheme();
  const active = props.rows.filter((r) => r.status !== 'canceled' && r.status !== 'no_show').length;
  const guests = countGuests(props.rows);

  const { occupied, total } = props.zones.reduce(
    (acc, z) => {
      if (!z.occupancy) return acc;
      return {
        occupied:
          acc.occupied + z.occupancy.slots.filter((s) => s.occupiedByReservationId !== null).length,
        total: acc.total + z.occupancy.slots.length,
      };
    },
    { occupied: 0, total: 0 },
  );

  return (
    <Card
      radius="xl"
      p="xl"
      mb="lg"
      style={{
        background: theme.other['heroBackground'] as string,
        color: tokens.color.heroText,
        boxShadow: theme.other['softShadow'] as string,
      }}
    >
      <Group justify="space-between" align="flex-end" wrap="wrap">
        <Stack gap={4}>
          <Text size="sm" fw={500} style={{ color: tokens.color.heroSubtext }}>
            {props.venueName} · {props.dateLabel}
          </Text>
          <Title order={1} style={{ color: tokens.color.heroText, fontSize: 32, margin: 0 }}>
            {props.appTitle}
          </Title>
          <Text size="sm" style={{ color: tokens.color.heroSubtext }}>
            {props.tagline}
          </Text>
        </Stack>
        <Group gap="xl">
          <Stat label="預約（24 小時內）" value={`${active} 筆`} />
          <Stat label="預計客人" value={`${guests} 位`} />
          <Stat
            label={`${props.unitLabel}使用（總計）`}
            value={total === 0 ? '—' : `${occupied} / ${total}`}
          />
          {props.zones.map((z) => {
            if (!z.occupancy) return null;
            const occ = z.occupancy.slots.filter((s) => s.occupiedByReservationId !== null).length;
            const tot = z.occupancy.slots.length;
            return (
              <Stat
                key={z.zone.resourceId}
                label={z.zone.zoneLabel}
                value={tot === 0 ? '—' : `${occ} / ${tot}`}
              />
            );
          })}
        </Group>
      </Group>
    </Card>
  );
}

function Stat({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <Stack gap={2} align="flex-end">
      <Text size="xs" style={{ color: tokens.color.heroSubtext }}>
        {label}
      </Text>
      <Text
        fw={700}
        style={{ fontSize: 26, lineHeight: 1, color: tokens.color.heroText }}
      >
        {value}
      </Text>
    </Stack>
  );
}
